import {
  CustomsCityProcessClearanceResponse,
  CustomsCityValidateClearanceSelectionRequest,
  CustomsCityValidateCustomsCityClearanceResponse,
} from '../models/customs-city-clearance.model';
/** Master lookup `name` values shown in Send for Clearance (Customs City). */
export const CUSTOMS_CITY_CLEARANCE_CHANNEL_NAMES = [
  'ACAS',
  'AAMS',
] as const;

/** Body `statusCode` from POST /v1/customsCity/processClearance (HTTP status matches). */
export const CustomsCityClearanceStatusCodes = {
  AllQueued: 200,
  PartialQueued: 207,
  NoneQueued: 422,
} as const;

/** Body `statusCode` from POST /v1/customsCity/validateCustomsCityClearance. */
export const CustomsCityValidateChannelStatusCodes = {
  AllEligible: 200,
  PartialEligible: 207,
  NoneEligible: 422,
} as const;

/** CBS `processClearancePayload` when present; else popup `selectedIds` / `isFromModule`. */
export function resolveCustomsCityClearanceSelectionPayload(
  processClearancePayload: CustomsCityValidateClearanceSelectionRequest | undefined,
  fallbackIds: number[] | undefined,
  fallbackIsFrom: string | undefined
): CustomsCityValidateClearanceSelectionRequest | null {
  const ids = processClearancePayload?.ids ?? fallbackIds;
  const isFrom = processClearancePayload?.isFrom ?? fallbackIsFrom;
  if (!ids?.length || !isFrom) {
    return null;
  }
  return { ids, isFrom };
}

/** Bag / manifest / MAWB multi-select clearance (PAC-4451); shipment excluded. */
export function isCustomsCityClearanceChannelValidationModule(
  isFrom: string
): boolean {
  return (
    isFrom === 'bag' || isFrom === 'manifest' || isFrom === 'mawb'
  );
}

export function filterCustomsCityClearanceChannels<
  T extends { name: string },
>(channels: T[]): T[] {
  const allowed = new Set<string>(CUSTOMS_CITY_CLEARANCE_CHANNEL_NAMES);
  return channels.filter(channel => allowed.has(channel.name));
}

export function normalizeClearanceChannelName(
  name: string
): 'ACAS' | 'AAMS' | null {
  const upper = name?.trim().toUpperCase();
  if (upper === 'ACAS' || upper === 'AAMS') {
    return upper;
  }
  return null;
}

export function parseCustomsCityValidateCustomsCityClearanceResponse(
  payload: unknown
): CustomsCityValidateCustomsCityClearanceResponse | undefined {
  if (!payload || typeof payload !== 'object') {
    return undefined;
  }
  return payload as CustomsCityValidateCustomsCityClearanceResponse;
}

export function parseCustomsCityClearanceResponse(
  payload: unknown
): CustomsCityProcessClearanceResponse | undefined {
  if (!payload || typeof payload !== 'object') {
    return undefined;
  }
  return payload as CustomsCityProcessClearanceResponse;
}

/** Fullman Error Log popup shape: section key → message lines. */
export function buildCustomsCityClearanceErrorLogData(
  response: CustomsCityProcessClearanceResponse
): Record<string, string[]> {
  const errorLogData = buildClearanceErrorsGroupedByHawb(response);
  if (Object.keys(errorLogData).length) {
    return errorLogData;
  }
  return { Errors: [response.message || 'No details returned.'] };
}

function buildClearanceErrorsGroupedByHawb(
  response: CustomsCityProcessClearanceResponse
): Record<string, string[]> {
  const errorLogData: Record<string, string[]> = {};
  const failed = response.data?.failed;

  if (failed?.length) {
    failed.forEach(row => {
      const hawbExt = row.hawbExt?.trim();
      const messages =
        row.errors
          ?.map(error => error.message?.trim())
          .filter((text): text is string => !!text) ?? [];
      if (!messages.length) {
        return;
      }
      const key = hawbExt ? `HawbEXT: ${hawbExt}` : 'Errors';
      errorLogData[key] = messages;
    });
    return errorLogData;
  }

  const summaryMessages =
    response.data?.summary?.uniqueErrors
      ?.map(error => error.message?.trim())
      .filter((text): text is string => !!text) ?? [];

  if (summaryMessages.length) {
    errorLogData['Errors'] = summaryMessages;
  }

  return errorLogData;
}

export function hasQueuedCustomsCityClearance(
  response: CustomsCityProcessClearanceResponse
): boolean {
  return (response.data?.summary?.totalQueued ?? 0) > 0;
}
